import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ShieldCheck } from "lucide-react";
import { inputCls, btnPrimary } from "./crm";

export default function LoginView({ onLogin, variant = "admin" }) {
  const copy = variant === "agent"
    ? { title: "Agent portal", heading: "Sign in to your pipeline", sub: "Use the e-mail and access code from your invite.", hero: "Your recruits. Your pipeline.", heroSub: "Every recruit routed to you lands here the moment they apply. Move them through the stages, log your calls, and keep your pipeline moving.", tiles: [["Recruits", "Routed to you"], ["Stages", "6-step pipeline"], ["Notes", "Every touchpoint"]], foot: "Looking for the admin console?", footHref: "/admin" }
    : { title: "Admin console", heading: "Sign in to your CRM", sub: "Use your Breeze admin credentials.", hero: "Recruiting command center.", heroSub: "Every application, every agent, every stage of the pipeline — in one place. Route leads round-robin, track follow-ups, and watch your team grow.", tiles: [["Pipeline", "6 stages"], ["Routing", "Round-robin"], ["Alerts", "Instant e-mail"]], foot: "Are you an agent?", footHref: "/agent" };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="crm-root min-h-screen grid lg:grid-cols-2 bg-[#f4f6fa]" data-testid="admin-login-page" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      <div className="hidden lg:flex flex-col justify-between bg-[#0b1528] p-12 text-white relative overflow-hidden">
        <div className="hero-grid-bg absolute inset-0 opacity-60" />
        <div className="relative flex items-center">
          <img src="/crm-logo.png" alt="Breeze Recruiting CRM" className="h-24 w-auto object-contain drop-shadow-[0_0_20px_rgba(0,198,239,0.35)]" data-testid="login-brand-logo" />
        </div>
        <div className="relative">
          <h2 className="text-3xl font-extrabold leading-tight">{copy.hero}</h2>
          <p className="mt-4 max-w-md text-sm text-slate-400">{copy.heroSub}</p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {copy.tiles.map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                <div className="mt-1 text-sm font-bold">{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-[#00C6EF]" /> Secured admin access</div>
      </div>

      <div className="flex flex-col items-center justify-center px-5 py-10 sm:py-12">
        <div className="mb-6 flex items-center justify-center rounded-2xl bg-[#0b1528] px-6 py-3 lg:hidden" data-testid="login-mobile-brand">
          <img src="/crm-logo.png" alt="Breeze Recruiting CRM" className="h-20 w-auto object-contain" />
        </div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0b1528] text-white"><Lock className="h-5 w-5" /></span>
          <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-600" data-testid="login-variant">{copy.title}</div>
          <h1 className="mt-1 text-2xl font-extrabold text-slate-900">{copy.heading}</h1>
          <p className="mt-1.5 text-sm text-slate-500">{copy.sub}</p>
          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">Email address</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className={inputCls} placeholder="you@company.com" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">{variant === "agent" ? "Password or access code" : "Password"}</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className={inputCls} placeholder="••••••••" />
            </div>
            {error && <p data-testid="admin-login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
            <button type="submit" disabled={busy} data-testid="admin-login-submit" className={`${btnPrimary} w-full py-3`}>{busy ? "Signing in…" : "Sign In"}</button>
          </form>
          <p className="mt-6 text-xs text-slate-500">{copy.foot} <a href={copy.footHref} data-testid="login-switch-portal" className="font-semibold text-sky-700 hover:underline">Sign in here</a></p>
        </motion.div>
      </div>
    </div>
  );
}
